import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setLawFirmBondsFilters,
  setLawFirmBondsPage,
  setLawFirmPreset,
  resetLawFirmBondsState,
} from "../../../store/slices/lawFirmBondsSlice";
import {
  resetArchivesState,
  setArchivesFilters,
  setArchivesPage,
} from "../../../store/slices/bondArchivesSlice";
import { apiClient } from "../../../api/httpClient";
import {
  applyArchiveBondFilters,
  applyBondFilters,
  formatArchiveBondRow,
  formatLawFirmBond,
  pickFilters,
} from "../helpers/lawFirmBondsHelpers";
import {
  LAW_FIRM_BONDS_MODES,
  getLawFirmBondsPageConfig,
} from "../helpers/lawFirmBondsPageConfig";
import {
  EMPTY_ARCHIVE_FILTERS,
  clearedLawFirmBondFilters,
} from "../helpers/lawFirmBondsFilterPresets";
import { BOND_TYPE_TO_SUBCATEGORIES } from "../../SHARED/bondPageUtils";

function filterArchiveItemsByBondTypes(items, selectedBondTypes) {
  if (!selectedBondTypes.length) return items;
  return items.filter((item) => selectedBondTypes.includes(item.bondType));
}

export default function useLawFirmBondsFilters(mode, { resetPagination }) {
  const isTeamMember = mode === LAW_FIRM_BONDS_MODES.TEAM_MEMBER;
  const pageConfig = getLawFirmBondsPageConfig(mode);
  const dispatch = useDispatch();
  const lawfirmId = useSelector((state) => state.lawfirm.lawfirmId);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const savedLawFirmFilters = useSelector((state) => state.lawFirmBonds?.filters);
  const savedArchiveFilters = useSelector((state) => state.bondArchives?.filters);
  const savedFilters = isTeamMember
    ? savedArchiveFilters || EMPTY_ARCHIVE_FILTERS
    : savedLawFirmFilters || clearedLawFirmBondFilters([]);

  const [filters, setFilters] = useState(savedFilters);

  const [bondTypes, setBondTypes] = useState([]);
  const [lawFirms, setLawFirms] = useState([]);
  const [attorneys, setAttorneys] = useState([]);
  const [professionalGuardians, setProfessionalGuardians] = useState([]);
  const [estates, setEstates] = useState([]);
  const [principals, setPrincipals] = useState([]);
  const [allBonds, setAllBonds] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [lawFirmBondAllowList, setLawFirmBondAllowList] = useState([]);
  const [loading, setLoading] = useState(false);

  const normalizeBondKey = (value) => {
    if (value === null || value === undefined) return "";
    return String(value).trim();
  };

  const bondMatchesAllowList = (bond, allowSet) => {
    if (!allowSet || allowSet.size === 0) return true;
    return (
      allowSet.has(normalizeBondKey(bond?.bond_number)) ||
      allowSet.has(normalizeBondKey(bond?.bond_id))
    );
  };

  const bondNumberOptions = useMemo(() => {
    if (isTeamMember) return [];
    const allowSet = new Set(
      Array.isArray(lawFirmBondAllowList)
        ? lawFirmBondAllowList.flatMap((b) =>
            [normalizeBondKey(b?.bond_number), normalizeBondKey(b?.bond_id)].filter(Boolean)
          )
        : []
    );

    const source = allowSet.size
      ? allBonds.filter((b) => bondMatchesAllowList(b, allowSet))
      : allBonds;

    return [...new Set(source.map((b) => b.bond_number).filter(Boolean))].map((num) => ({
      label: num,
      value: num,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allBonds, lawFirmBondAllowList, isTeamMember]);

  const clearLawFirmBondLists = () => {
    setLawFirmBondAllowList([]);
    setAllBonds([]);
    setFilteredData([]);
  };

  const loadLawFirmBondList = async (selectedLawFirm, overrideFilters) => {
    try {
      const lawFirmBondList = await apiClient(`/list-lawfirm-bonds/${lawfirmId}/`, "GET");
      const safeList = Array.isArray(lawFirmBondList) ? lawFirmBondList : [];
      setLawFirmBondAllowList(safeList);

      const formatted = safeList.map((bond) => formatLawFirmBond(bond));
      setAllBonds(formatted);

      const f = pickFilters(overrideFilters, selectedLawFirm, filters);
      setFilteredData(applyBondFilters(formatted, f));
    } catch (error) {
      console.error("Error fetching law firm bonds:", error);
      clearLawFirmBondLists();
    }
  };

  const fetchInitialData = async (overrideFilters = null) => {
    setLoading(true);
    try {
      if (isTeamMember) {
        const [bt, lf, bonds, pg] = await Promise.all([
          apiClient("/bondtypes/", "GET"),
          apiClient("/list-lawfirms/", "GET"),
          apiClient("/list-underwriting-bond-archives/", "GET"),
          apiClient("/get-all-professional-guardians", "GET"),
        ]);

        setBondTypes(
          bt
            .filter((b) => b.type !== "Guardian-Minor")
            .map((b) => ({ label: b.type, value: b.type }))
        );
        setProfessionalGuardians(
          (pg?.data || []).map((guardian) => ({
            label: guardian.full_name,
            value: guardian.pg_id,
          }))
        );

        const formatted = (Array.isArray(bonds) ? bonds : []).map((bond) =>
          formatArchiveBondRow(bond)
        );

        const distinctPrincipals = [...new Set(formatted.flatMap((b) => b.principals || []))]
          .filter(Boolean)
          .map((principal, index) => ({
            label: principal,
            bondType:
              formatted.find((b) => (b.principals || []).includes(principal))?.bond_type || "",
            value: `${principal}-${
              formatted.find((b) => (b.principals || []).includes(principal))?.bond_type || ""
            }-${index}`,
          }));
        const distinctEstates = [...new Set(formatted.map((b) => b.estate_name).filter(Boolean))]
          .map((estate, index) => ({
            label: estate,
            bondType: formatted.find((b) => b.estate_name === estate)?.bond_type || "",
            value: `${estate}-${
              formatted.find((b) => b.estate_name === estate)?.bond_type || ""
            }-${index}`,
          }));
        setPrincipals(distinctPrincipals);
        setEstates(distinctEstates);

        const distinctLawFirmNames = new Set(
          formatted.map((b) => b.law_firm_name).filter(Boolean)
        );
        setLawFirms(
          lf
            .filter((firm) => distinctLawFirmNames.has(firm.name))
            .map((f) => ({ label: f.name, value: f.lawfirm_id }))
        );

        const distinctAttorneys = [
          ...new Set(
            formatted
              .map((b) => b.attorney_name)
              .filter((n) => n != null && String(n).trim() !== "")
          ),
        ].map((name) => ({ label: name, value: name }));
        setAttorneys(distinctAttorneys);

        setAllBonds(formatted);
        const f = overrideFilters ?? filters ?? savedFilters;
        const result = await applyArchiveBondFilters(formatted, f);
        setFilteredData(result);
        return;
      }

      const [bt, lf, pr] = await Promise.all([
        apiClient("/bondtypes/", "GET"),
        apiClient("/list-lawfirms/", "GET"),
        apiClient("/principals", "GET"),
      ]);

      const lawFirmsData = lf.map((f) => ({ label: f.name, value: f.lawfirm_id }));
      setLawFirms(lawFirmsData);

      setBondTypes(
        bt.filter((b) => b.type !== "Guardian-Minor").map((b) => ({ label: b.type, value: b.type }))
      );
      setPrincipals(
        pr.map((p, index) => ({
          label: p.name,
          bondType: p.role,
          value: `${p.name}-${p.role}-${index}`,
        }))
      );

      if (!lawfirmId) {
        clearLawFirmBondLists();
        return;
      }

      const selectedLawFirm = lawFirmsData.find((firm) => firm.value === lawfirmId);
      if (!selectedLawFirm) {
        clearLawFirmBondLists();
        return;
      }

      setFilters((prev) => ({ ...prev, lawFirm: [selectedLawFirm] }));
      dispatch(setLawFirmPreset([selectedLawFirm]));
      await loadLawFirmBondList(selectedLawFirm, overrideFilters);
    } catch (err) {
      console.error("Error loading data", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTeamMember, lawfirmId]);

  useEffect(() => {
    if (isTeamMember || !lawfirmId || lawFirms.length === 0) return;
    const selectedLawFirm = lawFirms.find((firm) => firm.value === lawfirmId);
    if (selectedLawFirm) {
      setFilters((prev) => ({
        ...prev,
        lawFirm: [selectedLawFirm],
      }));
      dispatch(setLawFirmPreset([selectedLawFirm]));
      const allowSet = new Set(
        Array.isArray(lawFirmBondAllowList)
          ? lawFirmBondAllowList.flatMap((b) =>
              [normalizeBondKey(b?.bond_number), normalizeBondKey(b?.bond_id)].filter(Boolean)
            )
          : []
      );
      let result = [...allBonds].filter((b) => b.law_firm_name === selectedLawFirm.label);
      if (allowSet.size) {
        result = result.filter((b) => bondMatchesAllowList(b, allowSet));
      }
      setFilteredData(result);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lawfirmId, lawFirms, allBonds, dispatch, lawFirmBondAllowList, isTeamMember]);

  useEffect(() => {
    if (isTeamMember) return;
    if (filters.lawFirm.length > 0) {
      const firmIds = filters.lawFirm.map((f) => f.value);
      Promise.all(firmIds.map((id) => apiClient(`/lawfirm-user-location/${id}`, "GET"))).then(
        (responses) => {
          const merged = responses
            .flat()
            .filter((a) => a.Position === "Attorney")
            .map((a) => ({
              label: a.lawfirm_user_name,
              value: a.lawfirm_user_id,
            }));
          setAttorneys(merged);
        }
      );
    } else {
      setAttorneys([]);
    }
  }, [filters.lawFirm, isTeamMember]);

  const applyFilters = useCallback(async () => {
    if (isTeamMember) {
      const result = await applyArchiveBondFilters(allBonds, filters);
      setFilteredData(result);
      setFilterDialogOpen(false);
      resetPagination();
      dispatch(setArchivesFilters(filters));
      dispatch(setArchivesPage(0));
      return;
    }

    const result = applyBondFilters(allBonds, filters);
    setFilteredData(result);
    setFilterDialogOpen(false);
    resetPagination();
    const next = { ...filters };
    if (!next.lawFirm?.length && savedFilters?.lawFirm?.length) {
      next.lawFirm = savedFilters.lawFirm;
    }
    dispatch(setLawFirmBondsFilters(next));
    dispatch(setLawFirmBondsPage(0));
  }, [allBonds, dispatch, filters, isTeamMember, resetPagination, savedFilters]);

  const clearFilters = useCallback(() => {
    if (isTeamMember) {
      setFilters(EMPTY_ARCHIVE_FILTERS);
      dispatch(setArchivesFilters(EMPTY_ARCHIVE_FILTERS));
      resetPagination();
      dispatch(setArchivesPage(0));
      return;
    }

    const cleared = clearedLawFirmBondFilters(filters.lawFirm);
    setFilters(cleared);
    dispatch(setLawFirmBondsFilters(cleared));
    resetPagination();
    dispatch(setLawFirmBondsPage(0));
  }, [dispatch, filters.lawFirm, isTeamMember, resetPagination]);

  const dispatchArchiveFilters = useCallback(
    (next) => {
      setFilters(next);
      dispatch(setArchivesFilters(next));
    },
    [dispatch]
  );

  const selectedBondTypes = filters.bondType.map((b) => b.value);

  const availableSubcategories = selectedBondTypes.flatMap(
    (type) => BOND_TYPE_TO_SUBCATEGORIES[type] || []
  );

  const availablePrincipals = isTeamMember
    ? filterArchiveItemsByBondTypes(principals, selectedBondTypes)
    : [];

  const availableEstates = isTeamMember
    ? filterArchiveItemsByBondTypes(estates, selectedBondTypes)
    : [];

  /** Full refresh toolbar (clears filters; law firm preserves current firm chip). */
  const refreshToolbar = useCallback(async () => {
    resetPagination();
    if (isTeamMember) {
      setFilters(EMPTY_ARCHIVE_FILTERS);
      dispatch(resetArchivesState());
      await fetchInitialData(EMPTY_ARCHIVE_FILTERS);
      return;
    }
    const preservedLawFirm = filters.lawFirm;
    const cleared = clearedLawFirmBondFilters(preservedLawFirm);
    setFilters(cleared);
    dispatch(resetLawFirmBondsState());
    if (preservedLawFirm?.length) {
      dispatch(setLawFirmPreset(preservedLawFirm));
    }
    await fetchInitialData(cleared);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchInitialData is not memoized; matches prior monolith behavior
  }, [dispatch, filters.lawFirm, isTeamMember, resetPagination]);

  const applyTeamMemberFiltersSnapshot = useCallback(
    async (snapshot) => {
      const result = await applyArchiveBondFilters(allBonds, snapshot);
      setFilteredData(result);
      setFilterDialogOpen(false);
      resetPagination();
      dispatch(setArchivesFilters(snapshot));
      dispatch(setArchivesPage(0));
    },
    [allBonds, dispatch, resetPagination]
  );

  const onPendingApprovalChip = useCallback(async () => {
    const next = { ...filters, underwriting_status: ["pending"] };
    setFilters(next);
    await applyTeamMemberFiltersSnapshot(next);
  }, [applyTeamMemberFiltersSnapshot, filters]);

  const principalFilterOptions = useMemo(() => {
    if (isTeamMember) return [];
    const names = new Set();
    filteredData.forEach((b) => {
      (b.principals || []).forEach((p) => names.add(p));
    });
    (filters.principal || []).forEach((f) => {
      const label = typeof f === "object" && f !== null && "label" in f ? f.label : String(f);
      if (label) names.add(label);
    });
    return [...names]
      .sort((a, b) => String(a).localeCompare(String(b)))
      .map((name) => ({ label: name, value: name }));
  }, [filteredData, filters.principal, isTeamMember]);

  const estateFilterOptions = useMemo(() => {
    if (isTeamMember) return [];
    const names = new Set();
    filteredData.forEach((b) => {
      if (b.estate_name) names.add(b.estate_name);
    });
    (filters.estate || []).forEach((f) => {
      const label = typeof f === "object" && f !== null && "label" in f ? f.label : String(f);
      if (label) names.add(label);
    });
    return [...names]
      .sort((a, b) => String(a).localeCompare(String(b)))
      .map((name) => ({ label: name, value: name }));
  }, [filteredData, filters.estate, isTeamMember]);

  return {
    pageConfig,
    lawfirmId,
    filterDialogOpen,
    setFilterDialogOpen,
    filters,
    setFilters,
    bondTypes,
    lawFirms,
    attorneys,
    professionalGuardians,
    estates,
    principals,
    allBonds,
    filteredData,
    loading,
    bondNumberOptions,
    fetchInitialData,
    applyFilters,
    clearFilters,
    selectedBondTypes,
    availableSubcategories,
    availablePrincipals,
    availableEstates,
    dispatchArchiveFilters,
    principalFilterOptions,
    estateFilterOptions,
    refreshToolbar,
    onPendingApprovalChip,
  };
}
